import {
	Box,
	Button,
	FormControl,
	FormErrorMessage,
	Heading,
	HStack,
	Icon,
	Input,
	InputGroup,
	InputLeftElement,
	Link,
	IconButton,
	Checkbox,
	RadioGroup,
	SimpleGrid,
	Grid,
	Stack,
	Text,
	Textarea,
	VStack,
	useDisclosure
} from "@chakra-ui/react";
import { FaSearch, FaEnvelope, FaPhoneAlt, FaComments } from "react-icons/fa";
import FullScreenSection from "./FullScreenSection";
import LiveChat from "./LiveChat";
import { useMemo, useState } from "react";
import DownloadApp from "./DownloadApp";
import { resourceSections } from "./resourceFaqData";
import { renderFaqTextWithLinks } from "./faqTextLinks";

const resources = resourceSections.map((section) => ({
	title: section.title,
	description: section.description,
	href: `/resources#${section.id}`,
}));

export default function Help() {
	const [query, setQuery] = useState("");
	const [agree, setAgree] = useState("");
	const [touched, setTouched] = useState({
		firstName: false,
		lastName: false,
		email: false,
		message: false
	});
	const [values, setValues] = useState({
		firstName: "",
		lastName: "",
		email: "",
		message: ""
	});
	const { isOpen, onOpen, onClose } = useDisclosure();
	const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8001";

	const emailIsValid = /^\S+@\S+\.\S+$/.test(values.email);
	const firstNameError = touched.firstName && values.firstName.trim() === "";
	const lastNameError = touched.lastName && values.lastName.trim() === "";
	const emailRequiredError = touched.email && values.email.trim() === "";
	const emailFormatError = touched.email && values.email.trim() !== "" && !emailIsValid;
	const messageError = touched.message && values.message.trim() === "";

	const filteredResources = useMemo(() => {
		if (!query.trim()) return [];
		const lowerQuery = query.toLowerCase();
		return resources.filter((resource) =>
			`${resource.title} ${resource.description}`.toLowerCase().includes(lowerQuery)
		);
	}, [query]);

	const handleChange = (field) => (event) => {
		setValues((prev) => ({ ...prev, [field]: event.target.value }));
	};

	const handleBlur = (field) => () => {
		setTouched((prev) => ({ ...prev, [field]: true }));
	};

	return (
		<VStack spacing={0} width="100%">
			<FullScreenSection
				justifyContent="center"
				alignItems="center"
				isDarkBackground={false}
				backgroundColor="gray.50"
				py={100}
			>
				<VStack spacing={40} width="100%" maxW="1200px">
					<VStack spacing={4} textAlign="center" maxW="900px">
						<Heading fontSize={{ base: "2.2rem", md: "3rem" }} fontWeight="bold">
							How can we help?
						</Heading>
						<Box width="100%">
							<InputGroup size="lg">
								<InputLeftElement pointerEvents="none">
									<Icon as={FaSearch} color="gray.400" />
								</InputLeftElement>
								<Input
									placeholder="Search for your question"
									bg="white"
									value={query}
									onChange={(event) => setQuery(event.target.value)}
								/>
							</InputGroup>

							{filteredResources.length > 0 && (
								<Box mt={3} bg="white" borderRadius="lg" boxShadow="md" p={4} textAlign="left">
									<VStack spacing={3} align="stretch">
										{filteredResources.map((resource) => (
											<Box key={resource.title}>
												<Link href={resource.href} color="blue.600" fontWeight="semibold">
													<Text>{resource.title}</Text>
												</Link>
												<Text fontSize="sm" color="gray.600">
													{renderFaqTextWithLinks(resource.description)}
												</Text>
											</Box>
										))}
									</VStack>
								</Box>
							)}
						</Box>
					</VStack>
				
				<VStack spacing={8} width="100%">

					<VStack spacing={4} width="100%" align="flex-start">
						<Heading px = {{ base: 8, md: 10 }}  fontSize={{ base: "1.6rem", md: "2rem" }}>
							Or get in touch with our friendly team.
						</Heading>
					</VStack>

					<Grid templateColumns={{ base: "1fr", lg: "2.8fr 0.2fr" }} columnGap={{ base: 14, lg: 200 }} rowGap={12} width="100%">

						<Box bg="white" borderRadius="xl" boxShadow="lg" p={{ base: 6, md: 8 }} width="100%">
							<form
								onSubmit={async (e) => {
									e.preventDefault();
									// Send form data to backend
									try {
										const response = await fetch(`${API_BASE_URL}/api/help/`, {
											method: "POST",
											headers: {
												"Content-Type": "application/json",
												"X-Client-App": "swiftcredit",
											},
											body: JSON.stringify({
												firstName: values.firstName,
												lastName: values.lastName,
												email: values.email,
												message: values.message,
											}),
										});
										if (response.ok) {
											alert("Your message has been sent!");
											setValues({ firstName: "", lastName: "", email: "", message: "" });
											setTouched({ firstName: false, lastName: false, email: false, message: false});
											setAgree("");
										} else {
											alert("Failed to send message. Please try again.");
										}
									} catch (error) {
										alert("An error occurred. Please try again.");
									}
								}}
							>
								<Stack spacing={5}>
									<FormControl isInvalid={firstNameError}>
										<Input
											placeholder="First name"
											size="lg"
											bg="gray.50"
											value={values.firstName}
											onChange={handleChange("firstName")}
											onBlur={handleBlur("firstName")}
											required
										/>
										<FormErrorMessage>This field is required</FormErrorMessage>
									</FormControl>

									<FormControl isInvalid={lastNameError}>
										<Input
											placeholder="Last name"
											size="lg"
											bg="gray.50"
											value={values.lastName}
											onChange={handleChange("lastName")}
											onBlur={handleBlur("lastName")}
											required
										/>
										<FormErrorMessage>This field is required</FormErrorMessage>
									</FormControl>

									<FormControl isInvalid={emailRequiredError || emailFormatError}>
										<Input
											placeholder="Email"
											type="email"
											size="lg"
											bg="gray.50"
											value={values.email}
											onChange={handleChange("email")}
											onBlur={handleBlur("email")}
											required
										/>
										<FormErrorMessage>
											{emailRequiredError ? "This field is required" : "Enter a valid email"}
										</FormErrorMessage>
									</FormControl>

									<FormControl isInvalid={messageError}>
										<Textarea
											placeholder="Message"
											size="lg"
											bg="gray.50"
											minH="140px"
											value={values.message}
											onChange={handleChange("message")}
											onBlur={handleBlur("message")}
											required
										/>
										<FormErrorMessage>This field is required</FormErrorMessage>
									</FormControl>

									<FormControl isInvalid={agree === "" && touched.agree} isRequired>
													<Checkbox
														isChecked={agree === "agree"}
														onChange={e => {
															setAgree(e.target.checked ? "agree" : "");
															setTouched(prev => ({ ...prev, agree: true }));
														}}
														colorScheme="blue"
													>
														You agree to our <Link as={require('react-router-dom').Link} to="/privacy-policy" color="blue.600" textDecoration="underline">privacy policy</Link>
													</Checkbox>
										{agree === "" && touched.agree && (
											<FormErrorMessage>You must agree to our privacy policy</FormErrorMessage>
										)}
									</FormControl>

									<Button type="submit" size="lg" bg="black" color="white" borderRadius="full" _hover={{ bg: "gray.800" }}>
										Submit
									</Button>
								</Stack>
							</form>
						</Box>

						<VStack px = {{ base: 8, md: 10 }} align="flex-start" spacing={10} width="100%">
							<VStack align="flex-start" spacing={3}>
								<HStack spacing={3}>
									<Icon as={FaEnvelope} boxSize={6} color="gray.600" />
									<Heading fontSize="1.2rem">Email us</Heading>
								</HStack>
								<VStack align="flex-start" spacing={1}>
									<Text>support@swiftcredit.co</Text>
								</VStack>
							</VStack>

							<VStack align="flex-start" spacing={1}>
								<Heading fontSize="1.2rem">Mail Us</Heading>
								<Text>Swift Credit</Text>
								<Text>110060 Jasper Ave, Tower 1, Suite 2020. Edmonton, AB T5J 3R8</Text>
							</VStack>

							<VStack align="center" spacing={3} >
								
								<HStack spacing={3}>
									<HStack spacing={3}>
										<Icon as={FaPhoneAlt} boxSize={5} color="gray.600" />
										<Text fontWeight="semibold">+17802422478</Text>
									</HStack>
									<VStack align="flex-start" spacing={1}>
										<Link href="#" color="gray.600" fontWeight="semibold" onClick={onOpen}>
											<HStack spacing={3}>
												<Icon as={FaComments} boxSize={5} />
												<Text whiteSpace="nowrap">Chat</Text>
											</HStack>
										</Link>
									</VStack>
								</HStack>
							</VStack>
						</VStack>
					</Grid>
				</VStack>
				</VStack>

			<IconButton
				aria-label="Open live chat"
				icon={<FaComments size={36} />}
				position="fixed"
				bottom={{ base: 5, md: 6 }}
				right={{ base: 5, md: 6 }}
				borderRadius="full"
				bg="black"
				color="white"
				boxShadow="lg"
				boxSize={{ base: "50px", md: "58px" }}
				_hover={{ bg: "gray.800", transform: "scale(1.05)" }}
				onClick={onOpen}
				zIndex="tooltip"
			/>
			<LiveChat isOpen={isOpen} onClose={onClose} />
			</FullScreenSection>

		
		</VStack>
	);
}
